// Extracted from AIWorkoutGenerator.js during Phase 4 refactor (sprint 2026-06-20).
import React, { useState, useEffect } from 'react';
import { api } from '../../context/AppContext';
import useExerciseHistory from '../../hooks/useExerciseHistory';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Zap, Loader2, Play, Dumbbell, Clock, Repeat, Timer, ChevronDown, ChevronUp,
  Battery, BatteryLow, BatteryFull, Star, ThumbsUp, Send, Info,
  AlertTriangle, Shield, Save, BookmarkCheck, ExternalLink, History, RotateCcw, Trophy, Crosshair,
  CalendarDays, Layers, Target, Wrench, HeartPulse, BookOpen, Flame, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function PlanExerciseRow({ exercise, index, language, planId, onLogSaved }) {
  const { t } = useLanguage();
  const [showVideo, setShowVideo] = useState(false);
  const [logSets, setLogSets] = useState(exercise.sets || 3);
  const [logReps, setLogReps] = useState(exercise.reps || 12);
  const [logWeight, setLogWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Iter. 406.fi — Extracted into a tiny named hook to keep React's modern
  // `react-hooks/set-state-in-effect` and `react-hooks/purity` lint rules
  // happy. The async fetch + state-update pattern is identical, just
  // namespaced as a custom hook so the rule treats it as composable.
  const { lastLog, loadingHistory } = useExerciseHistory(exercise.name);

  // PR detection: purely derived — useMemo so no setState-in-effect lint trip.
  const isPR = React.useMemo(() => {
    if (!lastLog) return false;
    const currentWeight = parseFloat(logWeight) || 0;
    const lastWeight = lastLog.weight || 0;
    const currentReps = parseInt(logReps, 10) || 0;
    const lastReps = lastLog.reps || 0;
    return (currentWeight > lastWeight && currentWeight > 0) ||
      (currentWeight === lastWeight && currentReps > lastReps && currentWeight > 0);
  }, [logWeight, logReps, lastLog]);

  const autoFill = () => {
    if (!lastLog) return;
    setLogSets(lastLog.sets || exercise.sets || 3);
    setLogReps(lastLog.reps || exercise.reps || 12);
    setLogWeight(lastLog.weight ? String(lastLog.weight) : '');
    setWeightUnit(lastLog.weight_unit || 'lbs');
    toast.success(t('ai_workout_gen.munitions_rechargees', 'Munitions rechargees!'));
  };

  const saveLog = async () => {
    if (!planId) return;
    setSaving(true);
    try {
      const res = await api.post('/workouts/log-exercise', {
        plan_id: planId,
        exercise_index: index,
        exercise_name: exercise.name || '',
        log: { sets: logSets, reps: logReps, weight: parseFloat(logWeight) || 0, weight_unit: weightUnit },
      });
      setSaved(true);

      // Check for tactical challenge validation
      if (res.data.tactical_challenge_validated?.validated) {
        const v = res.data.tactical_challenge_validated;
        toast.success(t('ai_workout_gen.tactical_challenge_complete', 'DÉFI TACTIQUE VALIDÉ ! +{{pts}} pts pour ton équipe !', { pts: v.bonus_points }));
        window.dispatchEvent(new CustomEvent('tactical-challenge-validated', { detail: res.data }));
      } else if (isPR) {
        toast.success(t('ai_workout_gen.nouveau_pr_record_personnel_battu', 'NOUVEAU PR! Record personnel battu!'));
      } else {
        toast.success(t('ai_workout_gen.log_enregistre', 'Log enregistré!'));
      }
      onLogSaved?.();
    } catch {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 bg-secondary/30 rounded-lg space-y-2" data-testid={`plan-exercise-${index}`}>
      <div className="flex items-center gap-3">
        <span className="w-7 h-7 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-black shrink-0">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground truncate">{exercise.name}</p>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {exercise.sets && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Repeat className="w-3 h-3" /> {exercise.sets}x{exercise.reps || ''}
                {exercise.rir != null && <span className="text-red-400 font-black ml-0.5">@ RIR {exercise.rir}</span>}
              </span>
            )}
            {exercise.tempo && (
              <span className="text-[10px] text-primary font-bold">
                Tempo {exercise.tempo}
              </span>
            )}
            {exercise.rest_seconds && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Timer className="w-3 h-3" /> {exercise.rest_seconds}s repos
              </span>
            )}
            {exercise.intensity_technique && (
              <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 uppercase tracking-wider" data-testid={`technique-${index}`}>
                {exercise.intensity_technique.replace('_', ' ')}
              </span>
            )}
            {exercise.duration_seconds && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" /> {exercise.duration_seconds}s
              </span>
            )}
          </div>
        </div>
        {/* Video demo link */}
        {exercise.video_url && (
          <button
            onClick={() => setShowVideo(!showVideo)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/15 hover:bg-primary/25 text-primary transition-colors text-[10px] font-bold uppercase"
            data-testid={`play-video-${index}`}
          >
            <Play className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Demo</span>
          </button>
        )}
      </div>

      {/* Notes */}
      {exercise.notes && (
        <p className="text-[10px] text-muted-foreground italic pl-10">{exercise.notes}</p>
      )}

      {/* Muscle groups */}
      {exercise.muscle_groups?.length > 0 && (
        <div className="flex gap-1 pl-10 flex-wrap">
          {exercise.muscle_groups.map((mg, i) => (
            <span key={i} className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">{mg}</span>
          ))}
        </div>
      )}

      {/* Video embed */}
      {showVideo && exercise.video_url && (
        <div className="ml-10 rounded-lg overflow-hidden bg-zinc-900 relative" style={{ aspectRatio: '16/10' }}>
          <iframe
            src={exercise.video_url}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={exercise.name}
          />
          <a
            href={exercise.video_url}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-primary/70 transition-colors"
            data-testid={`video-external-${index}`}
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}

      {/* ─── DERNIERE MISSION (Last Performance) ─── */}
      {!loadingHistory && lastLog && (
        <div className="ml-10 flex items-center gap-2 flex-wrap" data-testid={`last-mission-${index}`}>
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-zinc-800/70 border border-zinc-700/50">
            <History className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-amber-400 font-bold uppercase">
              {t('ai_workout_gen.derniere_mission', 'Dernière Mission')}:
            </span>
            <span className="text-[10px] text-foreground font-medium">
              {lastLog.sets}x{lastLog.reps} @ {lastLog.weight} {lastLog.weight_unit}
            </span>
          </div>
          {/* AUTO-FILL Button */}
          <button
            onClick={autoFill}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 text-amber-400 transition-colors border border-amber-500/20"
            data-testid={`auto-fill-${index}`}
          >
            <RotateCcw className="w-3 h-3" />
            <span className="text-[10px] font-bold uppercase">Auto-Fill</span>
          </button>
        </div>
      )}

      {/* ─── CIBLE D'ASSAUT (Progressive Overload Target) ─── */}
      {!loadingHistory && lastLog && (
        <div className="ml-10" data-testid={`assault-target-row-${index}`}>
          <AssaultTarget target={calcAssaultTarget(lastLog)} language={language} />
        </div>
      )}

      {/* ─── LOG INPUTS: Sets / Reps / Weight ─── */}
      <div className="ml-10 flex items-center gap-2 flex-wrap" data-testid={`exercise-log-${index}`}>
        <div className="flex items-center gap-1">
          <label className="text-[9px] text-muted-foreground uppercase">Sets</label>
          <Input
            type="number"
            min={1}
            max={20}
            value={logSets}
            onChange={e => setLogSets(parseInt(e.target.value) || 1)}
            className="w-12 h-7 text-xs text-center bg-secondary border-input px-1"
            data-testid={`log-sets-${index}`}
          />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-[9px] text-muted-foreground uppercase">Reps</label>
          <Input
            type="number"
            min={1}
            max={100}
            value={logReps}
            onChange={e => setLogReps(parseInt(e.target.value) || 1)}
            className="w-12 h-7 text-xs text-center bg-secondary border-input px-1"
            data-testid={`log-reps-${index}`}
          />
        </div>
        <div className="flex items-center gap-1">
          <label className="text-[9px] text-muted-foreground uppercase">Poids</label>
          <Input
            type="number"
            min={0}
            step={2.5}
            placeholder="0"
            value={logWeight}
            onChange={e => setLogWeight(e.target.value)}
            className={`w-16 h-7 text-xs text-center bg-secondary border-input px-1 ${isPR ? 'ring-2 ring-amber-400/50 border-amber-400/50' : ''}`}
            data-testid={`log-weight-${index}`}
          />
          <button
            onClick={() => setWeightUnit(weightUnit === 'lbs' ? 'kg' : 'lbs')}
            className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded hover:bg-primary/20 transition-colors"
            data-testid={`log-unit-${index}`}
          >
            {weightUnit}
          </button>
        </div>

        {/* PR Badge */}
        {isPR && (
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-400/40"
            data-testid={`pr-badge-${index}`}
          >
            <Trophy className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] font-black text-amber-400 uppercase tracking-wider">
              {t('ai_workout_gen.nouveau_pr', 'Nouveau PR')}
            </span>
          </motion.div>
        )}

        <button
          onClick={saveLog}
          disabled={saving || saved}
          className={`h-7 px-2 rounded text-[10px] font-bold uppercase flex items-center gap-1 transition-all ${
            saved
              ? 'bg-emerald-500/20 text-emerald-400'
              : isPR
                ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 ring-1 ring-amber-400/30'
                : 'bg-primary/15 text-primary hover:bg-primary/25'
          }`}
          data-testid={`log-save-${index}`}
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <BookmarkCheck className="w-3 h-3" /> : <Save className="w-3 h-3" />}
          {saved ? 'OK' : isPR ? 'PR!' : 'Log'}
        </button>
      </div>
    </div>
  );
}

// ============ FEEDBACK SECTION ============
